<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <ClientDetailInsight
      :clientId="currClient.id"
      :initialPolicyId="currClient.policy?.id"
      @reset="handleReset"
      v-if="currClient"
    />

    <template v-else>
      <div class="client-insight-shell sba-slide-in">
      <div class="client-insight-head">
        <div>
          <label class="active teal-text">Client Insight</label>
          <h5>Client Utilization Review</h5>
          <p>
            Review premium received, utilization pressure, pending invoices, and MLR movement across clients.
          </p>
        </div>
        <span class="scope-chip">
          {{ selectedCat.toUpperCase() }}
          <em>{{ selectedYear || "Current / latest" }}</em>
        </span>
      </div>

      <div class="card-panel filter-panel z-depth-0">
        <div class="filter-title">
          <div>
            <label class="active teal-text">Scope</label>
            <h6>Client Filters</h6>
          </div>
          <i class="material-icons">tune</i>
        </div>
        <div class="filter-grid">
          <div>
            <div class="input-field">
              <input type="search" v-model="searchItem" @keyup.enter="handleSearch" placeholder="Search client, abbreviation or category" />
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="selectedYear" @change="handleSearch">
                <option value="">Current / latest</option>
                <option :value="year" v-for="year in years" :key="year">{{ year }}</option>
              </select>
              <label>Policy Start Year</label>
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="selectedCat" @change="handleSearch">
                <option :value="cat.value" v-for="cat in clientCats" :key="cat.value">{{ cat.label }}</option>
              </select>
              <label>Category</label>
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="selectedBand" @change="handleSearch">
                <option :value="band.value" v-for="band in bands" :key="band.value">{{ band.label }}</option>
              </select>
              <label>MLR Band</label>
            </div>
          </div>
          <div class="report-actions">
            <button class="btn-small waves-effect waves-light grey action-button" @click.prevent="handleClear">
              <i class="material-icons left">clear</i>Clear
            </button>
            <button class="btn-small waves-effect waves-light cyan darken-3 action-button" @click.prevent="handleSearch">
              <i class="material-icons left">search</i>Search
            </button>
            <button
              class="btn-small waves-effect waves-light cyan darken-3 action-button"
              :class="{ disabled: !clients.length }"
              @click.prevent="downloadClients"
            >
              <i class="material-icons left">download</i>Download
            </button>
          </div>
        </div>
      </div>

      <div class="secondary-panel">
        <div class="col s12 p-0">
          <p class="grey-text report-note">Gross MLR uses billed claims. Real MLR uses vetted approved claims.</p>
          <div class="summary-grid" v-if="summary">
            <div class="summary-tile clients">
              <small>Clients</small>
              <strong>{{ summary.clients_count }}</strong>
            </div>
            <div class="summary-tile premium">
              <small>Premium Received</small>
              <strong>{{ formatMoney(summary.invoice_paid) }}</strong>
            </div>
            <div class="summary-tile utilization">
              <small>Gross Utilization</small>
              <strong>{{ formatMoney(summary.gross_utilization ?? summary.utilization_sent) }}</strong>
              <small>{{ formatMoney(summary.real_utilization) }} real</small>
            </div>
            <div class="summary-tile mlr">
              <small>Gross MLR</small>
              <strong>{{ summary.gross_mlr ?? summary.utilization_rate }}%</strong>
              <small>{{ summary.real_mlr ?? 0 }}% real</small>
            </div>
          </div>
          <span class="legend-chip"><i class="legend-box paid"></i>Premium Received</span>
          <span class="legend-chip"><i class="legend-box pending"></i>Pending Invoices</span>
        </div>
      </div>

      <!-- <div class="row"> -->
        <div
          class="client-card-wrap clickable"
          v-for="client in clients"
          :key="client.id"
          @click.prevent="handleDetail(client)"
        >
          <div class="report-section client-card">
            <div class="client-row">
              <i class="material-icons" :class="{ danger: client.utilization_rate >= 100 }">
                {{ client.utilization_rate >= 100 ? "flag" : "business" }}
              </i>
              <div class="client-main">
                <strong>{{ client.name }}</strong>
                <small>{{ client.cat?.toUpperCase() || "" }} | {{ client.policy?.year || "" }} | {{ policyDateRange(client.policy) }}</small>
              </div>
              <div class="client-metrics">
                <div class="metric-block">
                  <small>Premium Received</small>
                  <strong>{{ formatMoney(client.invoice.paid) }}</strong>
                </div>
                <div class="metric-block">
                  <small>Gross Utilization</small>
                  <strong>{{ formatMoney(client.encounter.gross_sent ?? client.encounter.sent) }}</strong>
                  <small>{{ formatMoney(client.encounter.real_approved) }} real</small>
                </div>
                <div class="metric-block">
                  <small>Gross MLR</small>
                  <strong>{{ client.gross_mlr ?? client.utilization_rate }}%</strong>
                  <small>{{ client.real_mlr ?? 0 }}% real</small>
                </div>
                <div class="metric-block">
                  <small>Pending Invoices</small>
                  <strong>{{ formatMoney(client.invoice.pending) }}</strong>
                </div>
                <div class="metric-block">
                  <small>Encounters</small>
                  <strong>{{ client.encounter.count }}</strong>
                </div>
              </div>
              <div class="client-action">View</div>
            </div>
            <div class="measure-track" :title="`Gross MLR (${client.gross_mlr ?? client.utilization_rate}%)`">
              <span :class="barClass(client.gross_mlr ?? client.utilization_rate)" :style="`width: ${barWidth(client.gross_mlr ?? client.utilization_rate)}%`"></span>
            </div>
            <div class="invoice-stack" :title="`Premium received ${formatMoney(client.invoice.paid)} | Pending invoices ${formatMoney(client.invoice.pending)}`">
              <span class="paid" :style="`width: ${invoiceWidth(client.invoice.paid, client.invoice.generated)}%`"></span>
              <span class="pending" :style="`width: ${invoiceWidth(client.invoice.pending, client.invoice.generated)}%`"></span>
            </div>
            <small class="grey-text">Premium received / pending invoices</small>
          </div>
        </div>
      <!-- </div> -->

      <div class="section center" v-if="!clients.length">
        <h6>No Client Report <em>found</em>...</h6>
      </div>

      <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
      </div>
    </template>
  </template>
</template>

<script>
import { onMounted, onUpdated, ref, computed } from "vue";
import getItems from "@/composables/getItems";
import ClientDetailInsight from "@/components/insight/ClientDetailInsightComponent.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import M from "materialize-css";

export default {
  name: "clientsInsightComponent",
  components: {
    ClientDetailInsight,
    LoadingDiv,
    PaginationDiv,
  },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currClient = ref(null);
    const currUrl = ref("operator/insight/utilization/clients");
    const searchItem = ref("");
    const selectedYear = ref("");
    const selectedBand = ref("all");
    const selectedCat = ref("hmo");

    const bands = ref([
      { value: "all", label: "All" },
      { value: "low", label: "Low < 50%" },
      { value: "watch", label: "Watch 50-74%" },
      { value: "high", label: "High 75-99%" },
      { value: "over", label: "Over 100%" },
    ]);

    const clientCats = ref([
      { value: "hmo", label: "Private" },
      { value: "retail", label: "Retail" },
      { value: "tpa", label: "TPA" },
      // { value: "schemes", label: "Schemes" },
    ]);

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const years = computed(() => {
      const year = new Date().getFullYear();
      return [year, year - 1, year - 2, year - 3, year - 4];
    });

    const buildUrl = (page = null) => {
      const params = [];

      if (searchItem.value) {
        params.push(`q=${encodeURIComponent(searchItem.value)}`);
      }

      if (selectedYear.value) {
        params.push(`year=${selectedYear.value}`);
      }

      if (selectedCat.value) {
        params.push(`cat=${selectedCat.value}`);
      }

      if (selectedBand.value && selectedBand.value !== "all") {
        params.push(`band=${selectedBand.value}`);
      }

      if (page) {
        params.push(`page=${page}`);
      }

      return params.length ? `${currUrl.value}?${params.join("&")}` : currUrl.value;
    };

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const barWidth = (val) => {
      return Math.min(Number(val || 0), 100);
    };

    const barClass = (val) => {
      if (val >= 100) return "red darken-1";
      if (val >= 75) return "orange darken-1";
      if (val >= 50) return "purple darken-1";
      if (val > 0) return "green darken-1";
      return "blue darken-1";
    };

    const invoiceWidth = (val, total) => {
      if (!Number(total || 0)) {
        return 0;
      }

      return Math.min(Math.round((Number(val || 0) / Number(total || 0)) * 100), 100);
    };

    const formatDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const policyDateRange = (policy) => {
      if (!policy?.start_date || !policy?.end_date) return "";
      return `${formatDate(policy.start_date)} - ${formatDate(policy.end_date)}`;
    };

    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          error.value = null;
        } else {
          response.value = emptyClientsResponse;
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(() => {
      getItemsFromApi(buildUrl());
    });

    const handlePage = (page) => {
      isReady.value = false;
      getItemsFromApi(buildUrl(page));
    };

    const handleSearch = () => {
      isReady.value = false;
      getItemsFromApi(buildUrl());
    };

    const handleClear = () => {
      searchItem.value = "";
      selectedYear.value = "";
      selectedBand.value = "all";
      selectedCat.value = "hmo";
      isReady.value = false;
      getItemsFromApi(buildUrl());
    };

    const handleDetail = (val) => {
      currClient.value = val;
    };

    const handleReset = () => {
      currClient.value = null;
      getItemsFromApi(buildUrl());
    };

    const downloadClients = () => {
      const data = clients.value.map((client, index) => ({
        No: index + 1,
        Client: client.name,
        Category: client.cat?.toUpperCase() || "",
        Policy: client.policy?.year || "",
        "Policy Start Date": client.policy?.start_date || "",
        "Policy End Date": client.policy?.end_date || "",
        "Total Invoices": client.invoice.generated,
        "Premium Received": client.invoice.paid,
        "Pending Invoices": client.invoice.pending,
        Encounters: client.encounter.count,
        "Gross Utilization": client.encounter.gross_sent ?? client.encounter.sent,
        "Real Utilization": client.encounter.real_approved,
        "Gross MLR": `${client.gross_mlr ?? client.utilization_rate}%`,
        "Real MLR": `${client.real_mlr ?? 0}%`,
      }));

      if (data.length) {
        downloadExcelFx(data, `Client_Base_Utilization_${selectedYear.value || "Current_Latest"}_${selectedCat.value || "All"}_${selectedBand.value}`);
      }
    };

    const clients = computed(() => {
      return response.value?.data || [];
    });

    const allPages = computed(() => {
      return response.value || [];
    });

    const summary = computed(() => {
      return response.value?.summary || null;
    });

    const emptyClientsResponse = {
      data: [],
      links: [],
      current_page: 1,
      last_page: 1,
      from: null,
      to: null,
      total: 0,
      summary: {
        clients_count: 0,
        invoice_paid: 0,
        invoice_pending: 0,
        gross_utilization: 0,
        utilization_sent: 0,
        real_utilization: 0,
        gross_mlr: 0,
        utilization_rate: 0,
        real_mlr: 0,
      },
    };

    return {
      handlePage,
      handleSearch,
      handleClear,
      handleDetail,
      handleReset,
      downloadClients,
      formatMoney,
      policyDateRange,
      barWidth,
      barClass,
      invoiceWidth,
      isReady,
      error,
      currClient,
      searchItem,
      selectedYear,
      selectedBand,
      selectedCat,
      bands,
      clientCats,
      years,
      clients,
      allPages,
      summary,
    };
  },
};
</script>

<style scoped>
.spacer {
  padding: 2px;
}

.pointer {
  cursor: default;
}

.clickable {
  cursor: pointer;
}

.m-0 {
  margin: 0;
}

.p-0 {
  padding: 0;
}

.client-insight-shell {
  box-sizing: border-box;
  color: #263238;
  display: grid;
  gap: 16px;
  margin-top: 8px;
  width: 100%;
}

.client-insight-head,
.filter-panel,
.secondary-panel,
.report-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: none;
  max-width: 100%;
}

.client-insight-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 136px;
  padding: 28px;
}

.client-insight-head h5 {
  color: #263238;
  font-size: 1.24rem;
  font-weight: 650;
  margin: 4px 0 0;
}

.client-insight-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 760px;
}

.scope-chip {
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  box-shadow: none;
  color: #455a64;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.3;
  padding: 6px 10px;
  text-align: right;
}

.scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.filter-panel {
  background: #f7fafb;
  margin: 0;
  padding: 24px;
}

.filter-title {
  align-items: flex-start;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 16px;
}

.filter-title h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.filter-title i {
  color: #90a4ae;
  font-size: 24px;
}

.filter-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.35fr) repeat(3, minmax(0, 0.75fr));
  align-items: end;
}

.filter-grid > div {
  min-width: 0;
}

.filter-grid .input-field {
  margin: 0;
}

.report-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 1 / -1;
  justify-content: flex-end;
  width: 100%;
}

.action-button {
  display: flex;
  justify-content: center;
  margin: 0;
  white-space: nowrap;
}

.secondary-panel {
  margin-bottom: 14px;
  padding: 24px 24px 14px;
}

.report-section {
  animation: insightCardIn 0.24s ease both;
  margin-bottom: 12px;
  padding: 18px;
}

.report-note {
  margin: 0 0 10px;
}

.summary-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 14px;
}

.summary-tile {
  border-radius: 6px;
  color: #ffffff;
  min-height: 88px;
  padding: 16px;
}

.summary-tile.clients {
  background: #1565c0;
}

.summary-tile.premium {
  background: #00897b;
}

.summary-tile.utilization {
  background: #ef6c00;
}

.summary-tile.mlr {
  background: #c62828;
}

.summary-tile small,
.summary-tile strong {
  display: block;
}

.summary-tile small {
  color: rgba(255, 255, 255, 0.86);
}

.summary-tile strong {
  color: #ffffff;
  font-size: 1.18rem;
  font-weight: 700;
  margin-top: 7px;
  overflow-wrap: anywhere;
}

.client-card {
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.client-card-wrap {
  box-sizing: border-box;
  display: block;
  width: 100%;
}

.client-card:hover {
  border-color: #cfd8dc;
  box-shadow: 0 8px 22px rgba(38, 50, 56, 0.12);
  transform: translateY(-1px);
}

.client-row {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: 36px minmax(220px, 1.15fr) minmax(0, 2.2fr) 52px;
}

.client-row .material-icons {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  font-size: 21px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.client-row .material-icons.danger {
  background: #ffebee;
  color: #c62828;
}

.client-main strong {
  color: #263238;
  display: block;
  overflow-wrap: anywhere;
}

.client-main small {
  color: #607d8b;
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.client-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  min-width: 0;
}

.metric-block {
  min-width: 0;
}

.metric-block small {
  color: #607d8b;
  display: block;
}

.metric-block strong {
  color: #263238;
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.client-action {
  color: #00838f;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: right;
  text-transform: uppercase;
}

.legend-chip {
  color: #607d8b;
  display: inline-block;
  margin: 0 8px 10px 0;
}

.legend-box {
  display: inline-block;
  height: 9px;
  margin-right: 5px;
  width: 18px;
}

.legend-box.paid {
  background: #26a69a;
}

.legend-box.pending {
  background: #ffca28;
}

.invoice-stack {
  background: #eceff1;
  display: flex;
  height: 8px;
  margin-top: 10px;
  width: 100%;
}

.invoice-stack span {
  animation: insightBarIn 0.38s ease both;
  display: block;
  height: 8px;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.invoice-stack .paid {
  background: #26a69a;
}

.invoice-stack .pending {
  background: #ffca28;
}

.measure-track {
  background: #eceff1;
  border-radius: 10px;
  height: 8px;
  margin-top: 12px;
  overflow: hidden;
  width: 100%;
}

.measure-track span {
  animation: insightBarIn 0.38s ease both;
  display: block;
  height: 100%;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.measure-track:hover span,
.invoice-stack:hover span {
  filter: saturate(1.15);
}

@media only screen and (max-width: 1100px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .report-actions {
    grid-column: 1 / -1;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .client-row {
    grid-template-columns: 24px minmax(180px, 0.9fr) minmax(0, 1.8fr);
  }

  .client-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .client-action {
    grid-column: 3;
    text-align: left;
  }
}

@media only screen and (max-width: 992px) {
  .client-row {
    align-items: flex-start;
    grid-template-columns: 36px minmax(0, 1fr);
  }

  .client-main {
    min-width: 0;
  }

  .client-metrics {
    grid-column: 1 / -1;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-top: 4px;
  }

  .client-action {
    grid-column: 1 / -1;
    text-align: left;
  }
}

@media only screen and (max-width: 600px) {
  .client-insight-head {
    flex-direction: column;
  }

  .scope-chip {
    text-align: left;
  }

  .filter-grid,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .report-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }

  .client-row {
    align-items: flex-start;
    grid-template-columns: 36px minmax(0, 1fr);
  }

  .client-metrics {
    grid-column: 1 / -1;
    grid-template-columns: 1fr;
  }

  .metric-block,
  .client-action {
    grid-column: 1 / -1;
  }
}

@keyframes insightCardIn {
  from {
    opacity: 0.88;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes insightBarIn {
  from {
    opacity: 0.55;
    transform: scaleX(0.35);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
